import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface ExtractedText {
  name: string
  email: string
  id: string
  type: string
  userGroup: string
  job: string
  department: string
  permission: string
  sysAccountDetected: ''
}
export interface Annotation {
  annotateIndex: string
  evidenceAnnotationId: string
  jobId: string
  topLeftX: number
  topLeftY: number
  bottomRightX: number
  bottomRightY: number
  status: string
  // Whether this specific annotation has been verified (BE AnnotationDTO.Validated).
  // Undefined for annotations not yet loaded from the DB (e.g. mid-draft) — treated
  // as verified/neutral until we know otherwise.
  validated?: boolean
}
export interface AnnotateStatusState {
  evidenceId: string
  annotationTypeId: string
  annotationTypeLabel: string
  annotateList: Annotation[]
  annotateStatus: string
  isError: boolean
  errorText: string
  extractedText: ExtractedText[] | any[]
  pageNumber: number // Add pageNumber to track the current page
  isDirty: boolean
  originalChunks?: string[],
  supplementRequired?: boolean,
}

export const initialState: AnnotateStatusState[] = []

const AnnotateSlice = createSlice({
  name: 'Annotate',
  initialState,
  reducers: {
    addAnnotationList(
      state: AnnotateStatusState[],
      action: PayloadAction<any>,
    ) {
      // Upsert: add only entries not already tracked (avoid wiping fresh in-session annotations)
      action.payload.forEach((incoming: AnnotateStatusState) => {
        const incomingId = incoming.annotateList?.[0]?.evidenceAnnotationId
        const existing = incomingId
          ? state.find((s) => s.annotateList?.[0]?.evidenceAnnotationId === incomingId)
          : state.find(
            (s) =>
              s.evidenceId === incoming.evidenceId &&
              s.annotationTypeId === incoming.annotationTypeId &&
              s.pageNumber === incoming.pageNumber,
          )
        if (!existing) {
          state.push({ ...incoming, isDirty: false })
        } else if (
          existing.annotateStatus !== 'Completed' &&
          incoming.annotateStatus === 'Completed'
        ) {
          // Promote status and backfill extractedText from DB if not yet populated
          existing.annotateStatus = incoming.annotateStatus
          if (!existing.extractedText?.length && incoming.extractedText?.length) {
            existing.extractedText = incoming.extractedText
          }
          if (!existing.annotateList?.[0]?.evidenceAnnotationId && incoming.annotateList?.[0]?.evidenceAnnotationId) {
            existing.annotateList = incoming.annotateList
          }
        }
      })
    },
    addMarkAnnotate(state: AnnotateStatusState[], action: PayloadAction<any>) {
      const {
        evidenceId,
        annotationTypeId,
        annotationTypeLabel,
        annotateList,
        pageNumber
      } = action.payload
      state.push({
        evidenceId: evidenceId,
        annotationTypeId: annotationTypeId,
        annotationTypeLabel: annotationTypeLabel,
        annotateList: annotateList,
        annotateStatus: 'not-started',
        isError: false,
        errorText: '', // Add the missing errorText property
        extractedText: [], // Initialize extractedText as an empty array
        pageNumber: pageNumber, // Initialize pageNumber if needed
        isDirty: false,
        supplementRequired: false, // Initialize supplementRequired if needed
      })
    },
    updateMarkAnnotate(
      state: AnnotateStatusState[],
      action: PayloadAction<any>,
    ) {
      const {
        index,
        evidenceId,
        annotationTypeId,
        evidenceAnnotationId,
        jobId,
        pageNumber,
      } = action.payload
      const annotateEvidence = state.find(
        (item: AnnotateStatusState) =>
          item.annotationTypeId === annotationTypeId &&
          item.evidenceId === evidenceId &&
          item.pageNumber === pageNumber &&
          item.annotateList.some((a: any) => a.annotateIndex === index),
      )
      if (annotateEvidence) {
        annotateEvidence.annotateList = annotateEvidence.annotateList.map(
          (item: any) => {
            if (item.annotateIndex === index) {
              item.status = 'not-started'
              item.evidenceAnnotationId = evidenceAnnotationId
              item.jobId = jobId
              item.mappedTargetColumnId = null
            }
            return item
          },
        )
      }
    },
    updateJobStatus(state: AnnotateStatusState[], action: PayloadAction<any>) {
      const { annotationTypeId, status, jobId, pageNumber } = action.payload
      const annotateEvidence = state.find(
        (item: AnnotateStatusState) =>
          item.annotationTypeId === annotationTypeId &&
          item.pageNumber === pageNumber &&
          item.annotateList.some(
            (annotateItem: any) => annotateItem.jobId === jobId,
          ),
      )
      if (annotateEvidence) {
        annotateEvidence.annotateStatus = status
        annotateEvidence.pageNumber = pageNumber // Update the pageNumber if needed
        annotateEvidence.annotateList = annotateEvidence.annotateList.map(
          (item: any) => {
            if (item.jobId === jobId) {
              item.status = status
            }
            return item
          },
        )
      }
    },
    updateJobStatusByEvidenceId(
      state: AnnotateStatusState[],
      action: PayloadAction<any>,
    ) {
      const { evidenceId, status } = action.payload
      const annotateEvidence = state.filter(
        (item: AnnotateStatusState) => item.evidenceId === evidenceId,
      )
      if (annotateEvidence.length > 0) {
        annotateEvidence.forEach((item: AnnotateStatusState) => {
          item.annotateStatus = status
        })
      }
    },
    updateExtraxtedText(
      state: AnnotateStatusState[],
      action: PayloadAction<any>,
    ) {
      const { evidenceId, recordIndex, annotationTypeId, extractedText, pageNumber, evidenceAnnotationId } =
        action.payload
      const annotateEvidence = evidenceAnnotationId
        ? state.find((item: AnnotateStatusState) =>
          item.annotateList?.some((a: any) => a.evidenceAnnotationId === evidenceAnnotationId),
        )
        : state.find(
          (item: AnnotateStatusState) =>
            item.evidenceId === evidenceId &&
            item.annotationTypeId === annotationTypeId &&
            item.pageNumber === pageNumber,
        )
      if (annotateEvidence && annotateEvidence.extractedText[recordIndex]) {
        annotateEvidence.extractedText = annotateEvidence.extractedText.map(
          (item: any, index: number) => {
            if (index === recordIndex) {
              item = extractedText
            }
            return item
          },
        )
        annotateEvidence.isDirty = true
      } else if (annotateEvidence) {
        annotateEvidence.extractedText.push(extractedText)
        annotateEvidence.isDirty = true
      }
    },
    setExtractedText(
      state: AnnotateStatusState[],
      action: PayloadAction<any>,
    ) {
      const { evidenceId, annotationTypeId, extractedText, pageNumber, evidenceAnnotationId } = action.payload
      // Prefer matching by a specific evidenceAnnotationId (multi-region: each chip is one entry)
      const annotateEvidence = evidenceAnnotationId
        ? state.find((item: AnnotateStatusState) =>
          item.annotateList?.some((a: any) => a.evidenceAnnotationId === evidenceAnnotationId),
        )
        : state.find(
          (item: AnnotateStatusState) =>
            item.evidenceId === evidenceId &&
            item.annotationTypeId === annotationTypeId &&
            item.pageNumber === pageNumber,
        )
      if (annotateEvidence) {
        annotateEvidence.extractedText = extractedText
      }
    },
    removeAnnotationByEvidenceId(
      state: AnnotateStatusState[],
      action: PayloadAction<string>,
    ) {
      const evidenceId = action.payload
      const filtered = state.filter(
        (item: AnnotateStatusState) => item.evidenceId !== evidenceId,
      )
      state.length = 0
      filtered.forEach((item) => state.push(item))
    },
    storeChunksForAnnotation(
      state: AnnotateStatusState[],
      action: PayloadAction<{ evidenceAnnotationId: string; chunks: string[]; storeOriginal: boolean }>,
    ) {
      const { evidenceAnnotationId, chunks, storeOriginal } = action.payload
      const entry = state.find((item) =>
        item.annotateList?.some((a: any) => a.evidenceAnnotationId === evidenceAnnotationId),
      )
      if (entry) {
        if (storeOriginal && !entry.originalChunks) {
          entry.originalChunks = chunks
        }
      }
    },
    clearDirtyForEvidence(
      state: AnnotateStatusState[],
      action: PayloadAction<string>,
    ) {
      state
        .filter((item) => item.evidenceId === action.payload)
        .forEach((item) => {
          item.isDirty = false
          item.originalChunks = undefined // clear baseline so next open re-fetches fresh from API
        })
    },
    removeAnnotationByJobId(
      state: AnnotateStatusState[],
      action: PayloadAction<{
        evidenceId: string
        evidenceAnnotationId: string
      }>,
    ) {
      const { evidenceId, evidenceAnnotationId } = action.payload
      const index = state.findIndex(
        (item: AnnotateStatusState) =>
          item.evidenceId === evidenceId &&
          item.annotateList.some(
            (annotateItem: any) =>
              annotateItem.evidenceAnnotationId === evidenceAnnotationId,
          ),
      )
      if (index !== -1) {
        state.splice(index, 1)
      }
    },
    setSupplementRequired(
      state: AnnotateStatusState[],
      action: PayloadAction<{ value: boolean; evidenceId: string; pageNumber: number }>,
    ) {
      const { value, evidenceId, pageNumber } = action.payload
      state
        .filter((item) => item.evidenceId === evidenceId && item.pageNumber === pageNumber)
        .forEach((item) => {
          item.supplementRequired = value
        })
    }
  },
})

export const {
  addAnnotationList,
  addMarkAnnotate,
  updateMarkAnnotate,
  updateJobStatus,
  updateJobStatusByEvidenceId,
  updateExtraxtedText,
  setExtractedText,
  storeChunksForAnnotation,
  clearDirtyForEvidence,
  removeAnnotationByEvidenceId,
  removeAnnotationByJobId,
  setSupplementRequired,
} = AnnotateSlice.actions
export default AnnotateSlice.reducer
